import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

export interface Coords {
  latitude: number;
  longitude: number;
  /** Accuracy radius in meters, if provided by the device. */
  accuracy?: number;
}

export type PreciseLocationPermissionState =
  | "granted"
  | "denied"
  | "prompt"
  | "unavailable";

/**
 * Gets the device's current position (precise location).
 * - Android (Capacitor): uses native Geolocation with enableHighAccuracy (GPS).
 * - PWA/Web: uses W3C Geolocation API with enableHighAccuracy.
 *
 * Returns null if location is unavailable (permission denied, timeout, unsupported, or error).
 */
export async function getCurrentPosition(): Promise<Coords | null> {
  try {
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
    const coords: Coords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
    if (typeof position.coords.accuracy === "number") {
      coords.accuracy = position.coords.accuracy;
    }
    return coords;
  } catch {
    return null;
  }
}

/**
 * True when the app may use precise location (native permission granted, or web geolocation granted).
 * On web without the Permissions API, falls back to attempting a one-shot fix (non-null coords).
 */
export async function isPreciseLocationAccessGranted(): Promise<boolean> {
  const state = await getPreciseLocationPermissionState();
  return state === "granted";
}

/**
 * Returns precise location permission state for the current platform.
 */
export async function getPreciseLocationPermissionState(): Promise<PreciseLocationPermissionState> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { location } = await Geolocation.checkPermissions();
      if (location === "granted") return "granted";
      if (location === "denied") return "denied";
      return "prompt";
    } catch {
      return "unavailable";
    }
  }

  if (
    typeof navigator !== "undefined" &&
    navigator.permissions &&
    typeof navigator.permissions.query === "function"
  ) {
    try {
      const status = await navigator.permissions.query({
        name: "geolocation" as PermissionName,
      });
      if (status.state === "granted") return "granted";
      if (status.state === "denied") return "denied";
      return "prompt";
    } catch {
      return "unavailable";
    }
  }

  // Without Permissions API, browser geolocation may still prompt.
  return "prompt";
}

/**
 * Attempts to request precise location permission from the OS/browser prompt.
 * Returns true when access is granted after the request.
 */
export async function requestPreciseLocationAccess(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const current = await Geolocation.checkPermissions();
      if (current.location === "granted") return true;
    } catch {
      // Continue to request flow
    }

    try {
      const requested = await Geolocation.requestPermissions();
      if (requested.location === "granted") return true;
    } catch {
      // Some Android/Capacitor combinations fail to resolve callback here.
      // Fall back to a direct location read to trigger/check permission state.
    }

    const coords = await getCurrentPosition();
    if (coords) return true;

    try {
      const after = await Geolocation.checkPermissions();
      return after.location === "granted";
    } catch {
      return false;
    }
  }

  if (
    typeof navigator === "undefined" ||
    !navigator.geolocation ||
    typeof navigator.geolocation.getCurrentPosition !== "function"
  ) {
    return false;
  }

  return new Promise<boolean>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}
